import React, { useState } from "react";
import { Link as RouterLink, useLocation } from "react-router-dom";
import {
  Box,
  Container,
  Stack,
  Button,
  Link,
  IconButton,
  Drawer,
  Divider,
  List,
  ListItemButton,
  ListItemText,
  Typography,
} from "@mui/material";
import Iconify from "./Iconify";
import PromoBar from "./PromoBar";
import { ctaGlowSx } from "../theme/brand";
import { LOGIN_URL, REGISTER_URL } from "../config";

const NAV_LINKS = [
  ["Funktionen", "/#funktionen"],
  ["Neu", "/#neu"],
  ["Ablauf", "/#ablauf"],
  ["Preise", "/#preise"],
];

export default function SiteHeader() {
  const [open, setOpen] = useState(false);
  const { pathname } = useLocation();
  const onHome = pathname === "/";

  return (
    <>
      <PromoBar />
      <Box
      component="header"
      sx={{
        position: "sticky",
        top: 0,
        zIndex: 10,
        bgcolor: "rgba(255,255,255,0.92)",
        backdropFilter: "blur(8px)",
        borderBottom: "1px solid",
        borderColor: "grey.200",
      }}
    >
      <Container maxWidth="lg">
        <Stack
          direction="row"
          alignItems="center"
          justifyContent="space-between"
          sx={{ py: 1.5 }}
        >
          <Link
            component={RouterLink}
            to="/"
            sx={{ display: "flex", flexShrink: 0 }}
          >
            <Box
              component="img"
              src="/assets/logos/martialverselogodark.png"
              alt="MartialVerse"
              sx={{ height: { xs: 26, sm: 34 } }}
            />
          </Link>

          {onHome && (
            <Stack
              component="nav"
              aria-label="Hauptnavigation"
              direction="row"
              spacing={3}
              sx={{ display: { xs: "none", md: "flex" } }}
            >
              {NAV_LINKS.map(([label, href]) => (
                <Link
                  key={href}
                  href={href}
                  underline="none"
                  sx={{
                    color: "grey.700",
                    fontSize: 14,
                    fontWeight: 600,
                    "&:hover": { color: "primary.main" },
                  }}
                >
                  {label}
                </Link>
              ))}
            </Stack>
          )}

          <Stack
            direction="row"
            spacing={{ xs: 1, sm: 1.5 }}
            alignItems="center"
          >
            {!onHome && (
              <Button
                component={RouterLink}
                to="/"
                color="inherit"
                size="small"
                startIcon={<Iconify icon="mdi:arrow-left" />}
                sx={{ display: { xs: "none", sm: "inline-flex" } }}
              >
                Zur Startseite
              </Button>
            )}
            <Button
              href={LOGIN_URL}
              color="inherit"
              size="small"
              sx={{ display: { xs: "none", sm: "inline-flex" } }}
            >
              Login
            </Button>
            <Button
              href={REGISTER_URL}
              variant="contained"
              size="small"
              sx={{
                whiteSpace: "nowrap",
                flexShrink: 0,
                px: { xs: 1.5, sm: 2 },
                py: { xs: 0.5, sm: 0.75 },
                fontSize: { xs: 12.5, sm: 14 },
                ...ctaGlowSx,
              }}
            >
              Kostenlos testen
            </Button>
            {onHome && (
              <IconButton
                onClick={() => setOpen(true)}
                sx={{ display: { xs: "inline-flex", md: "none" } }}
                aria-label="Menü öffnen"
              >
                <Iconify icon="mdi:menu" width={24} />
              </IconButton>
            )}
          </Stack>
        </Stack>
      </Container>

      <Drawer
        anchor="right"
        open={open}
        onClose={() => setOpen(false)}
        PaperProps={{ sx: { width: 290 } }}
      >
        <Box
          sx={{ height: "100%", display: "flex", flexDirection: "column" }}
          role="presentation"
        >
          <Stack
            direction="row"
            alignItems="center"
            justifyContent="space-between"
            sx={{ pl: 2.5, pr: 1.5, py: 1.5 }}
          >
            <Box
              component="img"
              src="/assets/logos/martialverselogodark.png"
              alt="MartialVerse"
              sx={{ height: 24 }}
            />
            <IconButton
              onClick={() => setOpen(false)}
              aria-label="Menü schließen"
              sx={{
                color: "grey.600",
                bgcolor: "grey.100",
                "&:hover": { bgcolor: "rgba(91,79,212,0.12)", color: "primary.main" },
              }}
            >
              <Iconify icon="mdi:close" width={20} />
            </IconButton>
          </Stack>

          <Divider />

          <List sx={{ px: 1.5, py: 2, flexGrow: 1 }}>
            {NAV_LINKS.map(([label, href]) => (
              <ListItemButton
                key={href}
                component="a"
                href={href}
                onClick={() => setOpen(false)}
                sx={{
                  borderRadius: 2,
                  mb: 0.5,
                  "&:hover": { bgcolor: "rgba(91,79,212,0.08)", color: "primary.main" },
                }}
              >
                <ListItemText
                  primary={label}
                  primaryTypographyProps={{ fontWeight: 600 }}
                />
              </ListItemButton>
            ))}
            <ListItemButton
              component="a"
              href={LOGIN_URL}
              sx={{
                borderRadius: 2,
                mb: 0.5,
                "&:hover": { bgcolor: "rgba(91,79,212,0.08)", color: "primary.main" },
              }}
            >
              <ListItemText
                primary="Login"
                primaryTypographyProps={{ fontWeight: 600 }}
              />
            </ListItemButton>
          </List>

          <Box sx={{ p: 2.5, borderTop: "1px solid", borderColor: "grey.100" }}>
            <Button
              href={REGISTER_URL}
              variant="contained"
              fullWidth
              size="large"
              onClick={() => setOpen(false)}
              sx={{ py: 1.25, fontWeight: 700, ...ctaGlowSx }}
            >
              Kostenlos testen
            </Button>
            <Typography
              variant="caption"
              sx={{ display: "block", textAlign: "center", color: "grey.500", mt: 1.25 }}
            >
              1 Monat kostenlos, ohne Kosten.
            </Typography>
          </Box>
        </Box>
      </Drawer>
      </Box>
    </>
  );
}
